import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import { Spinner } from '@/components/common/Spinner';
import { ErrorState } from '@/components/common/ErrorState';
import { RowActionButtons } from '@/components/tables/RowActionButtons';
import { DataGrid } from '@/components/tables/DataGrid';
import type { DataGridColumn } from '@/components/tables/DataGrid';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { Select2 } from '@/components/forms/Select2';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import { providersApi } from '@/api/services/providers';
import type { ProviderDto } from '@/api/types/providers';
import { ApiError } from '@/api/client/abpError';
import { ProviderFormModal } from './ProviderFormModal';
import type { ProviderFormValues } from './ProviderFormModal';
import { ProviderPermissions } from './permissions';
import { useCreateProvider, useDeleteProvider, useProviderTypes, useUpdateProvider } from './api';

type ModalState =
  | { mode: 'create' }
  | { mode: 'edit'; id: string; initial: ProviderFormValues }
  | null;

export function ProvidersPage() {
  const can = usePermission();
  const canView = can(ProviderPermissions.view);
  const canCreate = can(ProviderPermissions.create);
  const canUpdate = can(ProviderPermissions.update);
  const canDelete = can(ProviderPermissions.delete);
  const canManageAccount = can(ProviderPermissions.manageAccount);

  const navigate = useNavigate();
  const toast = useToast();
  const typesQuery = useProviderTypes();
  const createMut = useCreateProvider();
  const updateMut = useUpdateProvider();
  const deleteMut = useDeleteProvider();

  const [modal, setModal] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProviderDto | null>(null);
  // Inline create row (legacy: a new provider is added directly in the table).
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: '', type: '', searchKeyword: '' });

  function startCreate() {
    setDraft({ name: '', type: '', searchKeyword: '' });
    setCreating(true);
  }
  function saveCreate() {
    if (!draft.name.trim()) {
      toast.error('Name is required.');
      return;
    }
    createMut.mutate(
      { name: draft.name.trim(), type: draft.type, searchKeyword: draft.searchKeyword },
      {
        onSuccess: () => {
          toast.success('New Provider Added Successfully');
          setCreating(false);
        },
        onError: (err) => reportError(err, 'Failed to add the provider.'),
      },
    );
  }

  const table = useServerDataTable<ProviderDto>({
    queryKey: ['providers', 'list'],
    fetcher: (req) => providersApi.getListForDataTable(req),
    pageSize: 5,
  });

  const typeOptions = useMemo(
    () => (typesQuery.data ?? []).map((t) => ({ value: t.name, label: t.name })),
    [typesQuery.data],
  );

  const columns: DataGridColumn<ProviderDto>[] = [
    { key: 'name', header: 'Name', sortable: true, filterable: true, filterKey: 'name', cell: (r) => r.name },
    { key: 'searchKeyword', header: 'Keyword', sortable: true, filterable: true, filterKey: 'searchKeyword', cell: (r) => r.searchKeyword },
    { key: 'type', header: 'Type', sortable: true, filterable: true, filterKey: 'type', cell: (r) => r.type },
  ];

  function reportError(err: unknown, fallback: string) {
    toast.error(err instanceof ApiError ? err.message : fallback);
  }

  function goToAccount(row: ProviderDto) {
    navigate(
      `/admin/providers/${encodeURIComponent(row.id)}/account?providerName=${encodeURIComponent(
        row.name ?? '',
      )}`,
    );
  }

  function handleSubmit(values: ProviderFormValues) {
    if (!modal) return;
    const payload = {
      name: values.name.trim(),
      type: values.type,
      searchKeyword: values.searchKeyword,
    };
    if (modal.mode === 'create') {
      createMut.mutate(payload, {
        onSuccess: () => {
          toast.success('New Provider Added Successfully');
          setModal(null);
        },
        onError: (err) => reportError(err, 'Failed to add the provider.'),
      });
    } else {
      updateMut.mutate(
        { id: modal.id, input: payload },
        {
          onSuccess: () => {
            toast.success('Provider updated.');
            setModal(null);
          },
          onError: (err) => reportError(err, 'Failed to update the provider.'),
        },
      );
    }
  }

  function confirmDelete() {
    if (!deleteTarget) return;
    deleteMut.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success('Provider removed.');
        setDeleteTarget(null);
      },
      onError: (err) => {
        reportError(err, 'Failed to remove the provider.');
        setDeleteTarget(null);
      },
    });
  }

  if (!canView) {
    return <ErrorState message="You do not have permission to view providers." />;
  }

  if (typesQuery.isLoading) return <Spinner />;
  if (typesQuery.isError)
    return <ErrorState message="Failed to load provider types." onRetry={() => typesQuery.refetch()} />;

  const showRowActions = canUpdate || canDelete || canManageAccount;

  return (
    <div>
      <PageHeader title="Providers" breadcrumb={['Home', 'Providers']} />

      <div className="card">
        <div className="card-body">
          <DataGrid
            controller={table}
            columns={columns}
            rowKey={(r) => r.id}
            emptyMessage="No providers found."
            toolbarEnd={
              canCreate && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  title="Create new Provider"
                  disabled={creating}
                  onClick={startCreate}
                >
                  <i className="fa fa-plus me-1" />
                  Create New
                </button>
              )
            }
            createRow={
              creating
                ? {
                    name: (
                      <input
                        className="form-control form-control-sm"
                        placeholder="Name"
                        autoFocus
                        value={draft.name}
                        onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                      />
                    ),
                    searchKeyword: (
                      <input
                        className="form-control form-control-sm"
                        placeholder="Keyword"
                        value={draft.searchKeyword}
                        onChange={(e) => setDraft((d) => ({ ...d, searchKeyword: e.target.value }))}
                      />
                    ),
                    type: (
                      <Select2
                       
                        placeholder="Type"
                        allowClear={false}
                        value={draft.type}
                        options={typeOptions}
                        onChange={(v) => setDraft((d) => ({ ...d, type: v ?? '' }))}
                      />
                    ),
                  }
                : undefined
            }
            createRowActions={
              <div className="d-inline-flex gap-1 justify-content-center">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  title="Save"
                  disabled={createMut.isPending}
                  onClick={saveCreate}
                >
                  <i className="fa fa-save" />
                </button>
                <button type="button" className="btn btn-sm btn-outline-danger" title="Cancel" onClick={() => setCreating(false)}>
                  <i className="fa fa-times" />
                </button>
              </div>
            }
            rowActions={
              showRowActions
                ? (row) => (
                    <div className="d-inline-flex gap-1 justify-content-center">
                      <RowActionButtons
                        onEdit={
                          canUpdate
                            ? () =>
                                setModal({
                                  mode: 'edit',
                                  id: row.id,
                                  initial: {
                                    name: row.name,
                                    type: row.type,
                                    searchKeyword: row.searchKeyword,
                                  },
                                })
                            : undefined
                        }
                        onDelete={canDelete ? () => setDeleteTarget(row) : undefined}
                      />
                      {/* Legacy shows a labelled "Edit Account" button (not an icon-only button). */}
                      {canManageAccount &&
                        (row.hasAccount ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-dark text-nowrap"
                            title="Edit Account"
                            onClick={() => goToAccount(row)}
                          >
                            <i className="fa fa-wallet me-1" />
                            Edit Account
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary text-nowrap"
                            title="Create Account"
                            onClick={() => goToAccount(row)}
                          >
                            <i className="fa fa-plus me-1" />
                            Create Account
                          </button>
                        ))}
                    </div>
                  )
                : undefined
            }
          />
        </div>
      </div>

      {modal && (
        <ProviderFormModal
          show
          mode={modal.mode}
          typeOptions={typeOptions}
          initial={modal.mode === 'edit' ? modal.initial : undefined}
          busy={createMut.isPending || updateMut.isPending}
          onSubmit={handleSubmit}
          onClose={() => setModal(null)}
        />
      )}

      <ConfirmDialog
        show={deleteTarget !== null}
        title="Remove provider"
        message={`Remove ${deleteTarget?.name ?? ''}? This cannot be undone.`}
        confirmLabel="Remove"
        busy={deleteMut.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
